import LoadFailure from '../../../components/Brand/LoadFailure';
import { EditIcon, SaveIcon } from '../../../components/Brand/Icon';
import usePageLayout from '../../../hooks/usePageLayout';
import useSessionIdentity, { getSessionIdentity } from '../../../hooks/useSessionIdentity';
import { waitForAllFeedVotes, waitForFeedVotes } from '../../../hooks/useFeedVote';
import '../UserResponsive.scss';
import './Profile.scss';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Button, Alert } from 'antd';
import BrandLoader from '../../../components/Brand/BrandLoader';
import ContentLimitStatus from '../../../components/Brand/ContentLimitStatus';
import LinkText from '../../../components/ExternalLink/LinkText';
import BioEditor from '../../../components/BioEditor';
import { measureAbout, contentLimitMessage } from '../../../utils/contentLimits';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import ButtonShare from '../../../components/Buttons/ButtonShare';
import ButtonSettings from '../../../components/Buttons/ButtonSettings';
import ButtonSubscribe from '../../../components/Buttons/ButtonSubscribe';
import ButtonFollowers from '../../../components/Buttons/ButtonFollowers';
import ButtonFollowing from '../../../components/Buttons/ButtonFollowing';
import UserImage from '../../../components/UserImage';
import StoryTitle from '../../../components/StoryTitle';
import WiwHeaderTitle from '../../../components/WiwHeaderTitle';
import { createName } from '../../../utils/func';
import api from '../../../services';
import { getUser } from '../../../utils/func';
import { APP_ROUTES } from '../../../utils/constant';
import url from '../../../utils/url';
import { profileShare } from '../../../utils/profileShare';
import ProfileBell from '../../../notifications/ProfileBell';
import { useNotifications } from '../../../notifications/NotificationsProvider';

const { Header, Content, Footer } = Layout;
const { userByUsername, userUpdateFields, storyList } = api;

export default function UserProfile() {
	const session = useSessionIdentity();
	return <SessionProfile key={session} session={session} />;
}

function SessionProfile({ session }) {
	let storageUser = getUser();
	const [result, setResult] = useState({ username: null, user: null, reactions: [], loading: true, error: false });
	const [attempt, setAttempt] = useState(0);
	const generation = useRef(0);
	const readController = useRef(null);
	const historyPending = useRef(false);
	const savingRef = useRef(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(false);
	const privacyPending = useRef(new Set());
	const [privacyBusy, setPrivacyBusy] = useState(new Set());
	let { username } = useParams();
	const [about, setAbout] = useState(null);
	const [editAbout, setEditAbout] = useState(false);
	const aboutInput = useRef(null);
	const current = result.username === username ? result : { user: null, reactions: [], loading: true };
	const user = current.user || {};
	const reactions = current.reactions;
	const [followingCount, setFollowingCount] = useState('');
	const [followingInitials, setFollowingInitials] = useState('');
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isOwnProfile = Boolean(storageUser && storageUser.id === user.id);
	const { enabled: notificationsEnabled } = useNotifications();
	const aboutLabel = t('settings:aboutYou', { defaultValue: 'About you' });
	const aboutMeasurement = measureAbout(about);

	const ref_header = useRef(null);
	const ref_footer = useRef(null);

	const { contentHeight, contentTop } = usePageLayout(ref_header, ref_footer);

	// A history retry belongs to the loaded author; it must not restart their
	// profile request or discard an in-progress biography edit.
	const loadHistory = useCallback(async (profileUser, request) => {
		if (generation.current !== request || session !== getSessionIdentity() || historyPending.current) return;
		historyPending.current = true;
		setResult(previous => ({ ...previous, loading: true, error: false }));
		try {
			if (profileUser.id === getUser()?.id) await waitForAllFeedVotes(readController.current?.signal);
			if (generation.current !== request || session !== getSessionIdentity()) return;
			const response = await storyList({ user_id: profileUser.id, activity: true }, { signal: readController.current?.signal });
			if (generation.current !== request || session !== getSessionIdentity()) return;
			if (!response?.success || !Array.isArray(response.list)) throw new Error('History unavailable');
			setResult(previous => ({ ...previous, reactions: response.list, loading: false, error: false }));
		} catch {
			if (generation.current === request && session === getSessionIdentity()) setResult(previous => ({ ...previous, loading: false, error: true }));
		} finally {
			if (generation.current === request && session === getSessionIdentity()) historyPending.current = false;
		}
	}, [session]);

	useEffect(() => {
		const request = ++generation.current;
		const controller = new AbortController();
		readController.current = controller;
		historyPending.current = false;
		savingRef.current = false;
		privacyPending.current = new Set();
		setPrivacyBusy(new Set());
		setSaving(false);
		setSaveError(false);
		setEditAbout(false);
		setAbout('');
		setResult({ username, user: null, reactions: [], loading: true, error: false });
		const load = async () => {
			try {
				const response = await userByUsername({ username }, { signal: controller.signal });
				if (generation.current !== request || session !== getSessionIdentity()) return;
				if (response?.statusCode === 404 || (response?.success && !response.user)) {
					navigate(url(APP_ROUTES.PAGE404), { replace: true });
					return;
				}
				if (!response?.success || !response.user) throw new Error('Profile unavailable');
				setFollowingCount(Number(response.user.followers_count) || 0);
				setFollowingInitials(Boolean(response.user.following));
				setAbout(response.user.about || '');
				// The author is ready; their history must not hold the whole
				// profile behind a second network round trip.
				setResult({ username, user: response.user, reactions: [], loading: true, error: false });
				await loadHistory(response.user, request);
			} catch {
				if (generation.current === request && session === getSessionIdentity()) setResult(previous => ({ ...previous, loading: false, error: true }));
			}
		};
		load();
		return () => { controller.abort(); if (generation.current === request) generation.current += 1; };
	}, [username, attempt, navigate, loadHistory, session]);

	const retryLoad = () => {
		if (current.user) loadHistory(current.user, generation.current);
		else setAttempt(value => value + 1);
	};

	const pageTitle = current.user ? `${t('user.profile_title')} ${createName(user)} @${user.user_name}` : t('user.profile_title');
	const shareProfile = profileShare(user);
	const pageDescription = current.user ? shareProfile.shareText : pageTitle;

	const onChangeAbout = value => {
		setAbout(value);
		setSaveError(false);
	}

	const startEditAbout = () => {
		if (!isOwnProfile || session !== getSessionIdentity()) return;
		// Mount and focus during the tap so mobile Safari can open its keyboard.
		flushSync(() => setEditAbout(true));
		aboutInput.current?.focus({ cursor: 'end', preventScroll: true });
	}

	const endEditAbout = async () => {
		if (savingRef.current || aboutMeasurement.error || !isOwnProfile || session !== getSessionIdentity()) return;
		const request = generation.current;
		savingRef.current = true;
		setSaving(true);
		setSaveError(false);
		try {
			const response = await userUpdateFields({ id: user.id, about: aboutMeasurement.text });
			if (generation.current !== request || session !== getSessionIdentity()) return;
			if (!response?.success) {
				setSaveError(response?.errorCode || true);
				return;
			}
			setAbout(aboutMeasurement.text);
			setResult(previous => ({ ...previous, user: { ...previous.user, about: aboutMeasurement.text } }));
			setEditAbout(false);
		} catch {
			if (generation.current === request && session === getSessionIdentity()) setSaveError(true);
		} finally {
			if (generation.current === request && session === getSessionIdentity()) { savingRef.current = false; setSaving(false); }
		}
	}

	const handleFollowChange = (isFollowing) => {
		setSaveError(false);
		setFollowingCount(prev => Math.max(0, (Number(prev) || 0) + (isFollowing ? 1 : -1)));
		setFollowingInitials(isFollowing);
	};

	const makeOpinionPublic = async reaction => {
		if (!isOwnProfile || !reaction.is_hidden || !reaction.id || current.loading
			|| session !== getSessionIdentity() || privacyPending.current.has(reaction.id)) return;
		const request = generation.current;
		const isCurrent = () => generation.current === request && session === getSessionIdentity();
		privacyPending.current.add(reaction.id);
		setPrivacyBusy(new Set(privacyPending.current));
		setSaveError(false);
		try {
			// A slider write accepted before leaving the feed must finish first.
			await waitForFeedVotes(reaction.story.id, readController.current?.signal);
			if (!isCurrent()) return;
			const response = await api.reactionPrivacy({ id: reaction.id, privat: false });
			if (!isCurrent()) return;
			if (!response?.success || response.is_hidden !== false) throw new Error('Visibility unavailable');
			// Revealing an opinion from the profile is not a new interaction.
			// Preserve the row's position and any newer like/activity timestamp.
			setResult(previous => ({ ...previous, reactions: previous.reactions.map(item =>
				item.id === reaction.id ? { ...item, is_hidden: false } : item) }));
		} catch {
			if (isCurrent()) setSaveError(true);
		} finally {
			if (isCurrent()) {
				privacyPending.current.delete(reaction.id);
				setPrivacyBusy(new Set(privacyPending.current));
			}
		}
	};

	const profileButtons = (() => {
		if (isOwnProfile) {
			return <>
				{editAbout ? (
					<Button type="text" aria-label={t('user.save_settings')} disabled={saving || Boolean(aboutMeasurement.error)} aria-busy={saving} icon={<SaveIcon />} onClick={endEditAbout} />
				) : (
					<Button type="text" aria-label={t('user.edit_profile', { defaultValue: 'Edit profile' })} icon={<EditIcon />} onClick={startEditAbout} />
				)}
				<ProfileBell userId={user.id} own onError={() => setSaveError(true)} />
				<ButtonSettings />

			</>
		} else {
			return <>
				<ButtonSubscribe
					user_id={user.id}
					initialIsFollowing={followingInitials}
					onFollowChange={handleFollowChange}
                    onFollowError={() => setSaveError(true)}
				/>
				<ProfileBell userId={user.id} onError={() => setSaveError(true)} />
			</>
		}
	})();

	const reactionItems = reactions.filter(reaction => reaction?.story?.id).map(reaction =>
		<StoryTitle key={reaction.story.id} storyId={reaction.story.id} title={reaction.story.title}
			msg={reaction.text_msg} scope={reaction.scope} reactionId={reaction.id} opinion={reaction} profileUsername={username}
			onMakePublic={isOwnProfile ? () => makeOpinionPublic(reaction) : undefined}
			privacyBusy={current.loading || privacyBusy.has(reaction.id)} />
	);

	return (
		<Layout className="app-layout user-page user-profile-page">
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
			</Helmet>
			<Header className="header" ref={ref_header}>
				<WiwHeaderTitle title={user.user_name || username} preserveCase />
				{current.user && <div className="wrapper-padding">
					<div className="profile-user-control">
						<div className="profile-user-avatar">
							<UserImage picture={user.picture} />
						</div>
						<div className="profile-user-control-button" style={{ '--profile-action-count': (isOwnProfile ? 5 : 4) + (notificationsEnabled ? 1 : 0) }}>
								{profileButtons}
								<ButtonFollowers username={username} count={followingCount} />
								<ButtonFollowing username={username} count={user.following_count} />
								<ButtonShare {...shareProfile} />
						</div>
					</div>
						<div className="profile-user-name">
							{createName(user)}
						</div>
				</div>}
			</Header>
			<Content className="content-layot profile_page wrapper-padding" style={{ height: contentHeight, top: contentTop }}>
				{current.loading && !current.user && <BrandLoader />}
				{current.error && <LoadFailure onRetry={retryLoad} recoverOnResume />}
				{saveError && <Alert type="error" role="alert" message={contentLimitMessage(saveError, t) || t('brand:saveError')} />}
				{current.user && <div className="profile-bio">
					{editAbout ? (
						<>
							<BioEditor ref={aboutInput} label={aboutLabel} describedBy="profile-about-limit"
								invalid={Boolean(aboutMeasurement.error)} disabled={saving} value={about} onChange={onChangeAbout} />
							<ContentLimitStatus id="profile-about-limit" measurement={aboutMeasurement} kind="about" />
						</>
					) : (
						about?.trim() || !isOwnProfile ? <LinkText value={about} /> : <span className="profile-bio-placeholder">{aboutLabel}</span>
					)}
				</div>}
				<div className="profile-events" aria-busy={current.loading}>
					{current.loading && current.user && <BrandLoader />}
					{reactionItems}
					{current.user && !current.loading && !current.error && !reactionItems.length && <p className="profile-events-empty" role="status">
						{isOwnProfile
							? t('settings:noActivity', { defaultValue: 'Your opinions will appear here' })
							: t('settings:noPublicOpinions', { defaultValue: 'No public opinions yet' })}
					</p>}
				</div>
			</Content>
			<Footer ref={ref_footer}>
				<ButtonPanelReaction />
			</Footer>
		</Layout>
	)
}
